import React, { useState, useMemo } from 'react';
import { View, StyleSheet, Text, Image, ScrollView, Platform, RefreshControl, useWindowDimensions } from 'react-native';
import type { ImageStyle } from 'react-native';
import { useRoute } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import NavBar from 'src/presentation/components/organisms/NavBar';
import EmptyState from 'src/presentation/components/molecules/EmptyState';
import Loader from 'src/presentation/components/atoms/Loader';
import TopicusListItem from 'src/presentation/components/molecules/TopicusListItem';
import Footer from 'src/presentation/components/organisms/Footer';
import { useFooterHeight } from 'src/presentation/hooks/useFooterHeight';
import { useContentWidth } from 'src/presentation/hooks/useContentWidth';
import { usePageFromRoute } from 'src/presentation/hooks/usePageFromRoute';
import { useEventStore } from 'src/domain/stores/useEventStore';
import Translations from 'src/core/utils/Translations';
import type { RootStackParamList } from 'src/presentation/navigation/types/NavigationTypes';
import Fonts from 'src/core/utils/Fonts';
import Dimens from 'src/core/utils/Dimens';
import Colors from 'src/core/utils/Colors';
import type Constants from 'src/core/utils/Constants';
import type { ApiPageFoodItem } from 'src/core/models/api/ApiPage';

function FoodScreen() {
	const route = useRoute<RouteProp<RootStackParamList, typeof Constants.screenFood>>();
	const eventStore = useEventStore();
	const [refreshing, setRefreshing] = useState(false);
	const footerHeight = useFooterHeight();
	const contentWidth = useContentWidth();
	// The featured image keeps its half-a-screen-tall look, so the height stays window-based.
	const { height: windowHeight } = useWindowDimensions();

	const sizedStyles = useMemo(
		(): { image: ImageStyle } => ({
			image: {
				width: contentWidth,
				height: windowHeight / 2,
			},
		}),
		[contentWidth, windowHeight],
	);

	const { page, isLoading } = usePageFromRoute(route?.params, { type: 'Food' });

	const foodPage = page?.type === 'Food' ? page : null;

	const title = foodPage?.name ?? '';
	const featuredTitle = foodPage?.data.featured_title ?? '';
	const featuredImage = foodPage?.data.featured_image ?? '';
	const fooditems = foodPage?.children ?? [];

	const handleRefresh = async () => {
		setRefreshing(true);
		const uuid = eventStore.activeEventUuid ?? page?.event_uuid ?? '';
		await eventStore.fetchEvent(uuid, true);
		setRefreshing(false);
	};

	const renderFoodList = (items: ApiPageFoodItem[]) => {
		const arrayLength = items.length - 1;
		return items.map((fooditem, index) => (
			<TopicusListItem
				data={fooditem}
				image={fooditem.data?.page_image}
				key={index}
				noBorderBottom={arrayLength === index}
				title={fooditem.name}
			/>
		));
	};

	return (
		<View style={{ flex: 1 }}>
			<NavBar
				backBtn={true}
				bgImage={eventStore.activeTheme.header_bg_image}
				bgcolor={eventStore.activeTheme.header_bg_color}
				color={eventStore.activeTheme.header_text_color}
				editBtn={false}
				statusBarStyle={eventStore.activeTheme.statusbar_color}
				title={title}
			/>
			<ScrollView
				contentContainerStyle={{ flexGrow: 1, paddingBottom: footerHeight }}
				refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />}
				style={[styles.container, { flex: 1 }]}
			>
				{isLoading && !foodPage ? (
					<Loader />
				) : fooditems.length === 0 ? (
					<EmptyState
						icon="restaurant-outline"
						message={Translations.textNoFoodMessage}
						title={Translations.textNoFoodTitle}
					/>
				) : (
					<>
						<Text style={[styles.heading, { color: eventStore.activeTheme.heading_text_color }]}>{title}</Text>
						<View renderToHardwareTextureAndroid={true} style={styles.list}>
							{renderFoodList(fooditems)}
						</View>
						{featuredImage ? (
							<>
								<Text style={[styles.heading, { color: eventStore.activeTheme.heading_text_color }]}>{featuredTitle}</Text>
								<Image resizeMode="cover" source={{ uri: featuredImage }} style={sizedStyles.image} />
							</>
						) : null}
					</>
				)}
			</ScrollView>
			<Footer
				backgroundColor={eventStore.activeTheme.footer_bg_color}
				backgroundImage={eventStore.activeTheme.footer_bg_image}
				backgroundType={eventStore.activeTheme.footer_bg_type}
			/>
		</View>
	);
}

export default FoodScreen;

const styles = StyleSheet.create({
	container: {
		backgroundColor: Platform.OS === 'android' ? Colors.white : undefined,
	},
	heading: {
		color: Platform.OS === 'android' ? Colors.blackPure : Colors.greyHeader,
		fontSize: Platform.OS === 'android' ? Dimens.fontSize14 : Dimens.fontSize20,
		paddingLeft: Platform.OS === 'android' ? Dimens.padding16 : Dimens.padding20,
		paddingRight: Platform.OS === 'android' ? Dimens.padding16 : Dimens.padding20,
		marginTop: Platform.OS === 'android' ? Dimens.margin20 : Dimens.margin20,
		marginBottom: Platform.OS === 'android' ? Dimens.margin10 : Dimens.margin10,
		fontFamily: Platform.OS === 'android' ? Fonts.montserratMedium : Fonts.montserratMedium,
		opacity: Platform.OS === 'android' ? 0.54 : undefined,
	},
	list: {
		backgroundColor: Colors.white,
		borderBottomWidth: Platform.OS === 'android' ? 0 : 0.5,
		borderTopWidth: Platform.OS === 'android' ? 0 : 0.5,
		borderColor: Colors.overlay15,
	},
});
